/**
 * Every phrase that mentions what a project counts, built from one word.
 *
 * WHY THIS EXISTS RATHER THAN `config.balanceLabel` AT EACH CALL SITE
 * -------------------------------------------------------------------
 * The label is cosmetic, which makes it exactly the kind of thing that rots: a
 * new screen interpolates the raw word and gets the grammar subtly wrong, or
 * forgets it entirely and hard-codes "stock" — and a project that renamed it to
 * "Samples" then reads "Samples" on four screens and "stock" on the fifth. Mixed
 * vocabulary is worse than one neutral word everywhere, because it reads as a
 * bug rather than as a setting.
 *
 * So no screen ever sees the raw label. They ask for a NAMED phrase, and adding
 * a surface means adding a field here, where every phrase is visible side by
 * side and the grammar can be checked against the others.
 *
 * CASE HANDLING is deliberately conservative. Naive title-casing turns
 * "SIM cards" into "Sim Cards", and naive lower-casing turns it into
 * "sim cards" — both wrong, and wrong in the admin's face. So: only the FIRST
 * character is ever touched, and not even that when the label carries internal
 * capitals, which is the tell for an acronym or a brand.
 */

export const DEFAULT_BALANCE_LABEL = 'Stock';

export interface BalancePhrases {
  /** Heading / tab / column title: "Stock", "Samples", "SIM cards" */
  noun: string;
  /** Mid-sentence: "stock", "samples", "SIM cards" */
  lower: string;
  /** The per-question control's heading. */
  trackWithThis: string;
  /** The project-level switch on the Products page. */
  showToPromoters: string;
  /** The collapsible panel on a task response, and the Running Count heading. */
  byStore: string;
  /** A product at a store with nothing left: "Out of stock". */
  outOf: string;
  /** The same, mid-sentence: "3 out of stock". */
  outOfLower: string;
  /** Empty state when a store has no movements yet. */
  nothingRecorded: string;
  /** Fetch failure. */
  couldNotLoad: string;
  /** The five stock_effect options, in the order they are offered. */
  effectNone: string;
  effectNoneBlurb: string;
  effectSet: string;
  effectIncrease: string;
  effectDecrease: string;
  effectRemove: string;
  effectRemoveBlurb: string;
}

/** True when anything after the first character is uppercase — "SIM", "iPhone". */
const hasInnerCaps = (s: string): boolean => /[A-Z]/.test(s.slice(1));

const asNoun = (label: string): string =>
  hasInnerCaps(label) ? label : label.charAt(0).toUpperCase() + label.slice(1);

const asLower = (label: string): string =>
  hasInnerCaps(label) ? label : label.charAt(0).toLowerCase() + label.slice(1);

/**
 * Build every phrase from the project's label.
 *
 * Blank, null and undefined all fall back to 'Stock', so a project that never
 * sets one keeps the wording it has always had.
 */
export const balancePhrases = (label?: string | null): BalancePhrases => {
  const clean = String(label ?? '').trim() || DEFAULT_BALANCE_LABEL;
  const noun = asNoun(clean);
  const lower = asLower(clean);

  return {
    noun,
    lower,
    trackWithThis: `Track running ${lower} with this`,
    showToPromoters: `Show running ${lower} to promoters`,
    // "by store", not "at a store": the panel lists every store at once now, so
    // the singular was quietly promising a picker that is no longer there.
    byStore: `Running ${lower} by store`,
    // Built from `lower` for both, never lower-cased afterwards, so an acronym
    // survives: "out of SIM cards", not "out of sim cards".
    outOf: `Out of ${lower}`,
    outOfLower: `out of ${lower}`,
    nothingRecorded: `Nothing recorded against ${lower} at this store yet.`,
    couldNotLoad: `Could not load ${lower} for this store.`,
    effectNone: 'Nothing',
    // "does not consume stock" reads correctly for a mass noun and for a plural
    // alike, which is why the phrasing avoids an article.
    effectNoneBlurb: `Recorded only — a demo count does not consume ${lower}`,
    effectSet: `Sets the ${lower}`,
    effectIncrease: `Adds to ${lower}`,
    effectDecrease: `Reduces ${lower}`,
    // "(not a sale)" is IN THE LABEL, not left to the blurb. This is the only
    // choice in the list an admin can pick wrongly in a way that silently
    // corrupts someone's incentive, so the warning has to survive being read at
    // a glance in a collapsed dropdown.
    effectRemove: `Removes from ${lower} (not a sale)`,
    effectRemoveBlurb: 'Transferred to another store, taken back by the client, '
      + 'damaged or expired. Reduces the balance, never counts as a sale',
  };
};
